// SRD 5.1 class budgets — client-side mirrors of the backend's build/level-up
// validation (backend: hearth/builder.py _spell_budgets, _MULTICLASS_PREREQS,
// class skill lists). Used to gate the CharacterBuilder and LevelUpModal so the
// UI can't offer picks the server will silently reject.
import type { AbilityKey, Scores } from "../lib/types";
import { ABILITY_KEYS } from "../lib/types";
import { SKILL_LABELS, STANDARD_ARRAY } from "../lib/srd";

// Class skill choices: how many skills the class grants, and the list to pick
// from (bard picks from any skill). Background skills are granted server-side.
export const ALL_SKILLS = Object.keys(SKILL_LABELS);
export const CLASS_SKILLS: Record<string, { count: number; list: string[] }> = {
  barbarian: { count: 2, list: ["animal-handling", "athletics", "intimidation", "nature", "perception", "survival"] },
  bard: { count: 3, list: ALL_SKILLS },
  cleric: { count: 2, list: ["history", "insight", "medicine", "persuasion", "religion"] },
  druid: { count: 2, list: ["arcana", "animal-handling", "insight", "medicine", "nature", "perception", "religion", "survival"] },
  fighter: { count: 2, list: ["acrobatics", "animal-handling", "athletics", "history", "insight", "intimidation", "perception", "survival"] },
  monk: { count: 2, list: ["acrobatics", "athletics", "history", "insight", "religion", "stealth"] },
  paladin: { count: 2, list: ["athletics", "insight", "intimidation", "medicine", "persuasion", "religion"] },
  ranger: { count: 3, list: ["animal-handling", "athletics", "insight", "investigation", "nature", "perception", "stealth", "survival"] },
  rogue: { count: 4, list: ["acrobatics", "athletics", "deception", "insight", "intimidation", "investigation", "perception", "performance", "persuasion", "sleight-of-hand", "stealth"] },
  sorcerer: { count: 2, list: ["arcana", "deception", "insight", "intimidation", "persuasion", "religion"] },
  warlock: { count: 2, list: ["arcana", "deception", "history", "intimidation", "investigation", "nature", "religion"] },
  wizard: { count: 2, list: ["arcana", "history", "insight", "investigation", "medicine", "religion"] },
};

// Cantrips known by class level (breakpoints at 1 / 4 / 10). null → the class
// has no cantrip budget.
const CANTRIPS_KNOWN: Record<string, [number, number, number]> = {
  bard: [2, 3, 4], cleric: [3, 4, 5], druid: [2, 3, 4],
  sorcerer: [4, 5, 6], warlock: [2, 3, 4], wizard: [3, 4, 5],
};
export function cantripsKnown(cls: string, level: number): number | null {
  const t = CANTRIPS_KNOWN[cls];
  if (!t) return null;
  return level >= 10 ? t[2] : level >= 4 ? t[1] : t[0];
}

// Spells KNOWN by class level, for the four known-casters. Prepared casters
// (cleric/druid/paladin/wizard) have no fixed known count — the backend leaves
// them uncapped — so they're absent here (null → uncapped).
const SPELLS_KNOWN: Record<string, number[]> = {
  //          L: 0  1  2  3  4  5  6  7  8  9 10 11 12 13 14 15 16 17 18 19 20
  bard:        [0, 4, 5, 6, 7, 8, 9, 10, 11, 12, 14, 15, 15, 16, 18, 19, 19, 20, 22, 22, 22],
  sorcerer:    [0, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 12, 13, 13, 14, 14, 15, 15, 15, 15],
  ranger:      [0, 0, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11],
  warlock:     [0, 2, 3, 4, 5, 6, 7, 8, 9, 10, 10, 11, 11, 12, 12, 13, 13, 14, 14, 15, 15],
};
export function spellsKnown(cls: string, level: number): number | null {
  const t = SPELLS_KNOWN[cls];
  if (!t) return null;
  return t[Math.max(0, Math.min(20, level))] ?? null;
}

// SRD 5.1 multiclass ability prerequisites. Each entry is a list of requirement
// GROUPS (all must hold, AND); within a group any listed ability at 13+ satisfies
// it (OR) — e.g. fighter is "STR 13 or DEX 13".
export const MULTICLASS_PREREQS: Record<string, string[][]> = {
  barbarian: [["str"]],
  bard: [["cha"]],
  cleric: [["wis"]],
  druid: [["wis"]],
  fighter: [["str", "dex"]],
  monk: [["dex"], ["wis"]],
  paladin: [["str"], ["cha"]],
  ranger: [["dex"], ["wis"]],
  rogue: [["dex"]],
  sorcerer: [["cha"]],
  warlock: [["cha"]],
  wizard: [["int"]],
};

// 5e: to add a level in a NEW class you must have 13 in the key ability of BOTH
// your current class(es) and the new one. `scores` are the character's final
// ability scores keyed by short name (str/dex/…).
export function multiclassPrereqOk(
  scores: Record<string, number>,
  existingClasses: string[],
  newClass: string,
): boolean {
  const toCheck = new Set([...existingClasses, newClass]);
  for (const ci of toCheck) {
    for (const group of MULTICLASS_PREREQS[ci] ?? []) {
      if (!group.some((ab) => (scores[ab] ?? 10) >= 13)) return false;
    }
  }
  return true;
}

// ── XP / level progression (mirrors backend hearth/rules.XP_THRESHOLDS) ──────
// The cumulative XP a character needs to REACH each total level.
export const XP_THRESHOLDS: Record<number, number> = {
  1: 0, 2: 300, 3: 900, 4: 2700, 5: 6500, 6: 14000, 7: 23000, 8: 34000,
  9: 48000, 10: 64000, 11: 85000, 12: 100000, 13: 120000, 14: 140000,
  15: 165000, 16: 195000, 17: 225000, 18: 265000, 19: 305000, 20: 355000,
};

// True when a character at `currentLevel` has earned enough XP to advance to the
// next level (and isn't already at the L20 cap). Drives the level-up star badge
// so it only lights up when the button will actually do something.
export function xpQualifiesForLevel(currentLevel: number, xp: number): boolean {
  if (currentLevel >= 20) return false;
  return xp >= (XP_THRESHOLDS[currentLevel + 1] ?? Infinity);
}

// ── Quick-build recommendations (skip-30-decisions newcomer path) ────────────
// Standard-array assignment priority per class: the array [15,14,13,12,10,8] is
// dealt out down this list, so the first ability gets 15, the next 14, and so on.
const QUICK_ABILITY_PRIORITY: Record<string, AbilityKey[]> = {
  barbarian: ["str", "con", "dex", "wis", "cha", "int"],
  bard:      ["cha", "dex", "con", "wis", "int", "str"],
  cleric:    ["wis", "con", "str", "cha", "dex", "int"],
  druid:     ["wis", "con", "dex", "int", "cha", "str"],
  fighter:   ["str", "con", "dex", "wis", "cha", "int"],
  monk:      ["dex", "wis", "con", "str", "int", "cha"],
  paladin:   ["str", "cha", "con", "wis", "dex", "int"],
  ranger:    ["dex", "wis", "con", "str", "int", "cha"],
  rogue:     ["dex", "con", "int", "wis", "cha", "str"],
  sorcerer:  ["cha", "con", "dex", "wis", "int", "str"],
  warlock:   ["cha", "con", "dex", "wis", "int", "str"],
  wizard:    ["int", "con", "dex", "wis", "cha", "str"],
};

// A recommended standard-array assignment for a class: primary ability gets the
// highest value, and so on down the class's priority list.
export function recommendedArray(classIndex: string): Record<AbilityKey, number> {
  const order = QUICK_ABILITY_PRIORITY[classIndex] ?? ABILITY_KEYS;
  const out = {} as Record<AbilityKey, number>;
  ABILITY_KEYS.forEach((ab) => (out[ab] = 8));
  order.forEach((ab, i) => (out[ab] = STANDARD_ARRAY[i] ?? 8));
  return out;
}

// A sensible default set of skill proficiencies for a class: the first `count`
// from its class skill list (kept within budget by the caller too).
export function recommendedSkills(classIndex: string): string[] {
  const cs = CLASS_SKILLS[classIndex];
  if (!cs) return [];
  return cs.list.slice(0, cs.count);
}

// The primary spellcasting ability per class (for surfacing "your best score").
export const CLASS_PRIMARY_ABILITY: Record<string, AbilityKey> = {
  barbarian: "str", bard: "cha", cleric: "wis", druid: "wis", fighter: "str",
  monk: "dex", paladin: "str", ranger: "dex", rogue: "dex", sorcerer: "cha",
  warlock: "cha", wizard: "int",
};

// Pick the first N items from a spell/cantrip option list (already filtered to
// the class list by the caller) as a quick recommended loadout.
export function firstN<T extends { index: string }>(list: T[], n: number | null): string[] {
  if (n == null || n <= 0) return [];
  return list.slice(0, n).map((s) => s.index);
}

// Re-exported so quick-build consumers can size their picks without another import.
export type QuickScores = Scores;
